import { useEffect, useState } from 'react';

import { PencilRuler, Plus, Coins } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import CaseCard from './components/CaseCard';
import DraftsModalContent from './components/DraftsModal';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateCase } from '@/api/mutations/useCreateCase';
import { useDeleteCase } from '@/api/mutations/useDeleteCase';
import { useCasesQuery } from '@/api/queries/useGetCases';
import { useGetMatter } from '@/api/queries/useGetMatter';
import { Button } from '@/components/atoms/new-button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import EmptyState from '@/components/molecules/EmptyState';
import { ConfirmationModal } from '@/components/molecules/Modals/Confirmation';
import CustomModal from '@/components/molecules/Modals/CustomModal';
import { MainPageWrapper } from '@/components/molecules/PageWrappers/MainPageWrapper';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Case, MatterType } from '@/types';

const CasesExplorer = () => {
  const { hasPermission } = useHasPermission();

  // State
  const [searchText, setSearchText] = useState('');
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [caseIdToDelete, setCaseIdToDelete] = useState('');
  const [isDraftsModalOpen, setIsDraftsModalOpen] = useState(false);
  const [isNewSearch, setIsNewSearch] = useState(false);
  const [cases, setCases] = useState<Case[]>([]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const pageQuery = parseInt(searchParams.get('page') || '0') || 0;
  const matterId = searchParams.get('matterId');

  const { isLoading: isLoadingCases, data: casesData } = useCasesQuery({ matterId, searchText, pageQuery });
  const { data: responseMatterData } = useGetMatter(matterId ?? '');

  const { mutate: createCase, isLoading: isCreating } = useCreateCase();

  const onDeleteError = () => {
    toast.error('Error deleting case. Try again later');
    setConfirmDeleteModalOpen(false);
    setCaseIdToDelete('');
  };

  const onDeleteCancel = () => {
    setCaseIdToDelete('');
    setConfirmDeleteModalOpen(false);
  };

  const onDelete = (caseId: string) => {
    setCaseIdToDelete(caseId);
    setConfirmDeleteModalOpen(true);
  };

  const { mutate: deleteCase, isLoading: isDeleting } = useDeleteCase({
    onError: onDeleteError,
  });

  useEffect(() => {
    if ((totalPages === null || casesData?.pageCount !== totalPages) && casesData?.pageCount != undefined) {
      setTotalPages(casesData?.pageCount || 1);
    }
    if (casesData?.totalCount !== totalCount) {
      setTotalCount(casesData?.totalCount ?? 0);
    }
    // Filter out "All Documents" case on legacy page
    setCases(casesData?.cases?.filter((c) => !c.isAllDocuments) ?? []);
  }, [casesData]);

  const onDeleteConfirm = () => {
    setConfirmDeleteModalOpen(false);
    // Remove the case from the cases array
    const updatedCases = cases.filter((c) => c.id !== caseIdToDelete);
    setCases(updatedCases);
    deleteCase({ caseId: caseIdToDelete, isSuperAdmin: hasPermission(Permission.CaseDeleteAny) });
    setCaseIdToDelete('');
    setConfirmDeleteModalOpen(false);
    toast.success('Case deleted', { richColors: true });
  };

  const handleViewDraft = (id: string) => {
    trackCustomEvent(TrackingEvents.ViewDraftCase, {
      matterId: matterId,
      caseId: id,
    });
    navigate(
      `/app/chronos/case-creator?caseId=${id}&mode=${responseMatterData?.type === MatterType.Personal ? 'light' : 'full'}`,
    );
    setIsDraftsModalOpen(false);
  };

  const handleViewCase = (id: string) => {
    navigate(`/app/chronos/case-editor/summary?caseId=${id}`);
  };

  // Pagination
  const handleGoToPreviousPage = () => {
    navigate(
      `/app/chronos/explore?matterId=${matterId}&page=${pageQuery - 1}${searchText ? `&searchText=${searchText}` : ''}`,
    );
  };
  const handleGoToNextPage = () => {
    navigate(
      `/app/chronos/explore?matterId=${matterId}&page=${pageQuery + 1}${searchText ? `&searchText=${searchText}` : ''}`,
    );
  };
  const goToPage = (value: number) => {
    navigate(`/app/chronos/explore?matterId=${matterId}&page=${value}${searchText ? `&searchText=${searchText}` : ''}`);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    goToPage(0);
    setIsNewSearch(true);
  };

  return (
    <MainPageWrapper>
      <h2 className="text-2xl font-semibold text-gray-900">Cases</h2>

      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Cases organise documents for analysis. Create a case to extract and analyse facts from your documents using
          Wexler's AI.
        </p>
        {responseMatterData?.tokenConsumption !== undefined && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex cursor-pointer items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-100">
                  <Coins className="h-4 w-4 text-yellow-500" />
                  <span className="text-xs text-gray-600">Credits</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-gray-900 p-3 text-white">
                <div className="flex gap-1">
                  <span className="font-medium">Credits used in this matter:</span>
                  <span className="font-semibold text-yellow-400">
                    {responseMatterData.tokenConsumption.toLocaleString()}
                  </span>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            data-testid="new-case-btn"
            variant="brand"
            size="sm"
            onClick={() => {
              trackCustomEvent(TrackingEvents.ViewCase, {
                matterId: matterId,
              });
              createCase(matterId);
            }}
            disabled={isCreating}
          >
            New Case
            <Plus className="h-4 w-4" />
          </Button>
          <Button data-testid="open-drafts-btn" variant="outline" size="sm" onClick={() => setIsDraftsModalOpen(true)}>
            Drafts
            <PencilRuler className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-8">
          <div className="flex flex-row items-center gap-6">
            <SearchBox value={searchText} onSearchCall={handleSearch} placeholder="Search cases..." />
          </div>

          {totalPages ? (
            <Pagination
              testid="cases"
              canGetNextPage={pageQuery < totalPages - 1}
              canGetPrevPage={pageQuery > 0}
              prevPage={handleGoToPreviousPage}
              nextPage={handleGoToNextPage}
              currentPage={pageQuery}
              noOfPages={totalPages}
              goToPage={goToPage}
              isNewSearch={isNewSearch}
              setIsNewSearch={setIsNewSearch}
            />
          ) : null}
        </div>
      </div>

      <div className="flex-1">
        {isLoadingCases ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(12)].map((_, idx) => (
              <div key={idx}>
                <Skeleton className="h-40 w-full bg-gray-50" />
              </div>
            ))}
          </div>
        ) : !responseMatterData || !cases || cases.length === 0 ? (
          <EmptyState title="No cases found" subtitle="Create your first case!" />
        ) : (
          <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {cases.map((caseItem, index) => (
              <div data-testid={`active-case-${index}`} data-case-id={caseItem.id} key={caseItem.id}>
                <CaseCard
                  caseObject={caseItem}
                  onClick={handleViewCase}
                  onDelete={onDelete}
                  isDeleting={caseIdToDelete === caseItem.id}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        title="Delete Case"
        description="Deleting cases is irreversible. Are you sure you want to proceed?"
        isLoading={isDeleting}
        isOpen={confirmDeleteModalOpen}
        handleClose={onDeleteCancel}
        onConfirm={onDeleteConfirm}
      />

      <CustomModal
        isOpen={isDraftsModalOpen}
        handleClose={() => setIsDraftsModalOpen(false)}
        content={
          <DraftsModalContent
            matterId={matterId ?? ''}
            onSelectDraft={handleViewDraft}
            onDeleteDraft={(id: string) =>
              deleteCase({ caseId: id, isSuperAdmin: hasPermission(Permission.CaseDeleteAny) })
            }
          />
        }
      />
    </MainPageWrapper>
  );
};

export default CasesExplorer;
